import { useState } from 'react';
import {
  Dialog,
  DropDown,
  DropDownOption,
  FlexColumn,
} from '@elementary/common-ui/components';
import { WAREHOUSE_DISPLAY_NAMES } from '@elementary/common-ui/constants/warehouses';
import { InputLabel, Typography } from '@mui/material';
import { fontSize } from '@elementary/common-ui/style/utils';
import useRuntime from '../hooks/useRuntime';
import LoadErrorMessage from '../LoadErrorMessage';
import { Runtime } from '../types';
import { toUpdateRuntimeRequest } from '../utils';
import WarehouseTypeIcon from '../WarehouseTypeIcon';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  runtime: Runtime;
};

const AddWarehousesDialog = ({ isOpen, onClose, runtime }: Props) => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const {
    warehouseConnections,
    isLoadingWarehouseConnections,
    isWarehouseConnectionsError,
    refetchWarehouseConnections,
    updateRuntime,
    isUpdating,
  } = useRuntime(runtime.id);

  const options: DropDownOption[] = warehouseConnections
    .filter((warehouse) => warehouse.runtimeId !== runtime.id)
    .map((warehouse) => ({
      value: warehouse.id,
      label: warehouse.name,
      icon: <WarehouseTypeIcon warehouseType={warehouse.warehouseType} />,
      disabled: !!warehouse.runtimeId,
      description: warehouse.runtimeId
        ? `Used by ${warehouse.runtimeName || 'another runtime'}`
        : WAREHOUSE_DISPLAY_NAMES[warehouse.warehouseType],
    }));

  const handleClose = () => {
    setSelectedIds([]);
    onClose();
  };

  const handleAdd = async () => {
    const request = toUpdateRuntimeRequest(runtime);
    await updateRuntime({
      ...request,
      warehouseConnectionIds: [
        ...request.warehouseConnectionIds,
        ...selectedIds,
      ],
    });
    handleClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      onConfirmButtonClick={handleAdd}
      title="Add warehouses"
      confirmText="Add"
      confirmDisabled={isUpdating || selectedIds.length === 0}
      isLoading={isUpdating}
      closeOnBackdropClick={false}
      message={
        <FlexColumn sx={{ gap: 1 }}>
          <InputLabel shrink>Warehouses</InputLabel>
          <DropDown
            multiple
            size="large"
            options={options}
            value={selectedIds}
            onChange={setSelectedIds}
            isLoading={isLoadingWarehouseConnections}
            showSearch={options.length > 5}
            showSelectAll={false}
            placeholder="Select warehouses"
          />
          <Typography variant="caption" sx={{ fontSize: fontSize('sm') }}>
            {isWarehouseConnectionsError ? (
              <LoadErrorMessage
                message="Couldn't load warehouses."
                onRetry={refetchWarehouseConnections}
              />
            ) : (
              'A warehouse can be used by one runtime at a time.'
            )}
          </Typography>
        </FlexColumn>
      }
    />
  );
};

export default AddWarehousesDialog;
